/**
 * Sidebar — Real-mode CRM sidebar.
 *
 * Renders the approved IA (OVERVIEW / PIPELINE / SCHEDULING & MEETINGS /
 * TOOLS / SYSTEM — or OVERVIEW / MY WORK / SYSTEM for agent roles) from the
 * shared `@/lib/sidebarSpec`. Strictly single-role (`user.role`) — no role
 * switcher, no multi-role preview.
 *
 * Delegates to the legacy marketing sidebar when the current path is a
 * Marketing Hub route.
 *
 * Safety:
 *   - Unknown / missing role falls back to `read_only` (never `super_admin`).
 *   - Add Lead / Client, Meetings, Tasks removed per the approved spec
 *     (Tasks still shows as a MY WORK item for call/demo/bookings/contracts
 *     agents only — that's baked into SIDEBAR_SPEC and not exposed here).
 */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { canAccessMarketing } from "@/lib/marketingAccess";
import MarketingSidebarNav from "@/components/marketing/MarketingSidebarNav";
import {
  LayoutDashboard, LogOut, Megaphone, LayoutGrid, Plus, ChevronDown, ChevronRight,
} from "lucide-react";
import {
  ROLES, ROLE_ALIASES, normalizeRole, shouldShowAppSwitcher,
  roleBadge,
} from "@/lib/permissions";
import {
  SIDEBAR_SPEC, ROUTE, ICON, LABEL, NEW_OPTIONS, MASTER_SIDEBAR_SECTIONS,
} from "@/lib/sidebarSpec";
import { useCapabilities, SIDEBAR_CAP_KEY, canFromMap, RESTRICTIVE_FALLBACK } from "@/lib/capabilities";
import { useRuntimeConfig } from "@/lib/runtimeConfig";
import TrainingModeToggle from "@/components/training/TrainingModeToggle";

const CLIENT_DIALER_ROLES = ["super_admin", "crm_admin", "sales_manager", "sales_agent", "call_agent"];

const EMBLEM_URL = "https://customer-assets.emergentagent.com/job_285b778d-268b-4f9f-806e-ab953b551b56/artifacts/8tp297eu_Enable%20Healthcare%20emblem_BLUE.png";

/* ── "+ New" button with role-aware dropdown. Mirrors SidebarDemo's variant
 *    so both sidebars render an identical affordance. ── */
function NewButton({ variant }) {
  const options = NEW_OPTIONS[variant] || [];
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  if (options.length === 0) return null;
  return (
    <div ref={ref} style={{ padding: "0 12px", margin: "12px 0", position: "relative" }}
         data-testid="sidebar-new-wrap">
      <button
        onClick={() => setOpen(v => !v)}
        data-testid="sidebar-new"
        aria-expanded={open}
        style={{
          width: "100%", background: "#2563eb", color: "#fff", border: "none",
          borderRadius: 8, padding: "8px 12px", fontSize: 13, fontWeight: 500,
          display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
          justifyContent: "flex-start",
        }}
      >
        <Plus style={{ width: 14, height: 14 }} /> New
      </button>
      {open && (
        <div data-testid="sidebar-new-menu"
          style={{
            position: "absolute", top: "100%", left: 12, right: 12, marginTop: 4,
            background: "#fff", border: "0.5px solid #dddddd", borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.10)", padding: 6, minWidth: 200,
            zIndex: 50,
          }}>
          {options.map(o => (
            <button
              key={o.key}
              onClick={() => { setOpen(false); navigate(o.to); }}
              data-testid={`sidebar-new-${o.key}`}
              style={{
                width: "100%", background: "none", border: "none",
                padding: "8px 12px", borderRadius: 6, fontSize: 13,
                display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
                textAlign: "left", color: "#333",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#f5f5f5"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <o.Icon style={{ width: 14, height: 14, color: o.tint }} />
              <span>{o.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* Active-path matcher — supports routes with query strings (e.g. Tasks). */
function isActivePath(location, routePath) {
  const [pathPart, queryPart] = routePath.split("?");
  if (pathPart === "/") {
    if (location.pathname !== "/") return false;
  } else if (location.pathname !== pathPart && !location.pathname.startsWith(pathPart + "/")) {
    return false;
  }
  if (!queryPart) return true;
  const wanted = new URLSearchParams(queryPart);
  const have = new URLSearchParams(location.search);
  for (const [k, v] of wanted) if (have.get(k) !== v) return false;
  return true;
}

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const caps = useCapabilities();

  const isMarketingMode = location.pathname.startsWith("/marketing");

  // Phase-1 hardening: pull the runtime feature-flag at the very top
  // of the component so it's called unconditionally on every render
  // (rules-of-hooks). Used by `visibleByCapability` below.
  const { audienceBuilderEnabled } = useRuntimeConfig();

  // Safety: unknown / missing role falls back to read_only, NEVER super_admin.
  const rawRole = user?.role;
  const aliased = rawRole ? (ROLE_ALIASES[rawRole] || rawRole) : null;
  const role = aliased && SIDEBAR_SPEC[aliased] ? aliased : "read_only";
  const roleDef = ROLES[role] || ROLES.read_only;
  const roleBadgeColors = roleBadge(role);
  // Marketing module is a super-admin-only prototype (see
  // `lib/marketingAccess.js`). The CRM⇄Marketing switcher must therefore be
  // hidden from everyone else even if `shouldShowAppSwitcher` would
  // otherwise allow them. `canAccessMarketing` already AND-combines the
  // build-time env flag with the runtime role check.
  const showSwitcher = shouldShowAppSwitcher(role) && canAccessMarketing(role);

  /* Defense-in-depth gate: an item is only visible when BOTH the role-spec
   * allows it AND the backend capability map says true. Keys with no mapped
   * capability (dashboard/settings) are always allowed by the spec. */
  const visibleByCapability = (key) => {
    // CCM Dialer sandbox is a super-admin-only developmental tool. Hidden from
    // the sidebar for now (still reachable by direct route /ccm-dialer).
    if (key === "ccmDialer") return false;
    // Hardening (Apr 2026): hide audiences nav when the backend feature
    // flag is off — the page would error anyway since /api/audiences
    // returns 404 in that mode.
    if (key === "audiences" && !audienceBuilderEnabled) return false;
    const capKey = SIDEBAR_CAP_KEY[key];
    if (!capKey) return true;
    return canFromMap(caps, capKey);
  };

  const handleModeSwitch = (mode) => {
    if (mode === "marketing" && !isMarketingMode) navigate("/marketing/plan/overview");
    else if (mode === "crm" && isMarketingMode) navigate("/");
  };

  const getInitials = (name) => (name || "U").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

  /* ── CRM-mode rendering — capability-driven (May 2026).
   *
   * Iterates the MASTER_SIDEBAR_SECTIONS union and shows ONLY the items the
   * caller's effective capability map allows. This replaces the legacy
   * "static per-role section.keys list" behavior: super-admins granting a
   * cap on the Roles & Permissions page now translate to a sidebar item
   * appearing immediately on the affected user's next page load — no code
   * change required.
   *
   * Sections collapse entirely when none of their keys are visible (so an
   * agent-only role still sees a clean shell, just with fewer items). */
  const spec = SIDEBAR_SPEC[role];
  // Nested groups: Demo Bookings (Lead/Client Bookings) and Sales Dialer (Leads/Clients Dialer).
  // Clients Dialer is role-gated; hidden while permissions are unavailable (restrictive fallback).
  const capsLoaded = !!caps && caps !== RESTRICTIVE_FALLBACK;
  const GROUPS = {
    demoBookings: [
      { key: "lead-bookings", label: "Lead Bookings", path: ROUTE.demoBookings, show: visibleByCapability("demoBookings") },
      { key: "client-bookings", label: "Client Bookings", path: "/ehi-demo-bookings", show: visibleByCapability("ehiClients") },
    ].filter((c) => c.show),
    dialer: [
      { key: "leads-dialer", label: "Leads Dialer", path: ROUTE.dialer, show: visibleByCapability("dialer") },
      { key: "clients-dialer", label: "Clients Dialer", path: "/clients-dialer", show: capsLoaded && CLIENT_DIALER_ROLES.includes(normalizeRole(user?.role)) },
    ].filter((c) => c.show),
  };
  const groupActive = (g) => (GROUPS[g] || []).some((c) => isActivePath(location, c.path));
  const [openGroups, setOpenGroups] = useState(() => ({ demoBookings: groupActive("demoBookings"), dialer: groupActive("dialer") }));
  const activeKey = groupActive("demoBookings") ? "demoBookings" : groupActive("dialer") ? "dialer" : null;
  useEffect(() => { if (activeKey) setOpenGroups((o) => ({ ...o, [activeKey]: true })); }, [activeKey]);
  const renderGroup = (g) => {
    const Icon = ICON[g] || LayoutDashboard;
    const open = !!openGroups[g];
    const Chevron = open ? ChevronDown : ChevronRight;
    const testBase = `nav-${LABEL[g].toLowerCase().replace(/[\s&/]+/g, "-")}`;
    return (
      <div key={g} data-testid={`${testBase}-group`}>
        <button type="button" onClick={() => setOpenGroups((o) => ({ ...o, [g]: !o[g] }))} aria-expanded={open}
          className="sidebar-nav-item" data-testid={testBase}
          style={{ width: "100%", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
            ...(spec.primary === g ? { background: "rgba(37,99,235,0.06)", borderLeft: "3px solid #2563eb", fontWeight: 500 } : {}) }}>
          <Icon style={{ width: 16, height: 16, flexShrink: 0 }} />
          <span style={{ flex: 1 }}>{LABEL[g]}</span>
          <Chevron style={{ width: 13, height: 13, opacity: 0.6 }} />
        </button>
        {open && GROUPS[g].map((c) => (
          <Link key={c.key} to={c.path} data-testid={`nav-${c.key}`}
            className={`sidebar-nav-item ${isActivePath(location, c.path) ? "active" : ""}`}
            style={{ paddingLeft: 38, fontSize: 12.5 }}>
            <span>{c.label}</span>
          </Link>
        ))}
      </div>
    );
  };
  const renderCRM = () => MASTER_SIDEBAR_SECTIONS.map(section => {
    const visibleKeys = section.keys.filter((k) => (GROUPS[k] ? GROUPS[k].length > 0 : visibleByCapability(k)));
    if (visibleKeys.length === 0) return null;
    return (
      <div key={section.label} className="sidebar-section">
        <div className="sidebar-section-title" data-testid={`sidebar-section-${section.label}`}>
          {section.label}
        </div>
        {visibleKeys.map(key => {
          if (GROUPS[key]) return renderGroup(key);
          const Icon = ICON[key] || LayoutDashboard;
          const path = ROUTE[key];
          const active = isActivePath(location, path);
          const isPrimary = spec.primary === key;
          return (
            <Link
              key={key}
              to={path}
              className={`sidebar-nav-item ${active ? "active" : ""}`}
              data-testid={`nav-${LABEL[key].toLowerCase().replace(/[\s&\/]+/g, "-")}`}
              data-primary={isPrimary ? "true" : undefined}
              style={isPrimary ? {
                background: "rgba(37,99,235,0.06)",
                borderLeft: "3px solid #2563eb",
                fontWeight: 500,
              } : undefined}
            >
              <Icon style={{ width: 16, height: 16, flexShrink: 0 }} />
              <span>{LABEL[key]}</span>
              {/* Ambient count badges (aiAgents=3, resources=226) are demo-only
                  seed. Real users see no badges — enforced by seededData test. */}
            </Link>
          );
        })}
      </div>
    );
  });

  return (
    <aside className="sidebar" data-testid="sidebar" data-variant="real" data-role={role}>
      {/* App Mode Switcher (CRM ⇄ Marketing) */}
      {showSwitcher && (
        <div className="app-switcher" data-testid="app-switcher">
          <button className={`app-switcher-btn ${!isMarketingMode ? "active" : ""}`}
            onClick={() => handleModeSwitch("crm")} data-testid="app-switcher-crm">
            <LayoutGrid style={{ width: 14, height: 14 }} /> <span>CRM</span>
          </button>
          <button className={`app-switcher-btn ${isMarketingMode ? "active" : ""}`}
            onClick={() => handleModeSwitch("marketing")} data-testid="app-switcher-marketing">
            <Megaphone style={{ width: 14, height: 14 }} /> <span>Marketing</span>
          </button>
        </div>
      )}

      {/* Logo */}
      <div className="sidebar-logo">
        <Link to={isMarketingMode ? "/marketing/plan/overview" : "/"} style={{ display: "flex", alignItems: "center", gap: 9, textDecoration: "none" }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, overflow: "hidden", flexShrink: 0 }}>
            <img src={EMBLEM_URL} alt="Enable" style={{ width: 28, height: 28, objectFit: "contain" }} />
          </div>
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontSize: 14, fontWeight: 650, color: "var(--text-primary)" }}>
              {isMarketingMode ? "Marketing Hub" : "CRM"}
            </div>
            <div style={{ fontSize: 9.5, color: "var(--text-tertiary)" }}>
              {isMarketingMode ? "Enable Healthcare" : "Healthcare Platform"}
            </div>
          </div>
        </Link>
      </div>

      {/* + New button (real mode, CRM-only, role-gated) */}
      {!isMarketingMode && spec.new && <NewButton variant={spec.new} />}

      {/* Navigation */}
      <nav className="sidebar-nav">
        {isMarketingMode ? <MarketingSidebarNav /> : renderCRM()}
      </nav>

      {/* Footer */}
      <div className="sidebar-footer">
        <TrainingModeToggle />
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 2px" }} data-testid="sidebar-user-panel">
          <div style={{
            width: 30, height: 30, borderRadius: "50%",
            background: roleBadgeColors.bg, display: "flex", alignItems: "center", justifyContent: "center",
            color: roleBadgeColors.color, fontSize: 11, fontWeight: 600
          }}>
            {getInitials(user?.full_name)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 560, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {user?.full_name || "User"}
            </div>
            <span data-testid="sidebar-role-badge" data-role={role} style={{
              fontSize: 9, fontWeight: 600, padding: "1px 6px", borderRadius: 3,
              background: roleBadgeColors.bg,
              color: roleBadgeColors.color
            }}>
              {roleDef.label || role}
            </span>
          </div>
          <button onClick={() => { logout(); navigate("/login"); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 4 }}
            data-testid="logout-btn" title="Sign out">
            <LogOut style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>
    </aside>
  );
}
